<template>
  <v-app :style="{ background: vuetify.theme.themes[theme].primary }">
    <v-navigation-drawer v-if="currentUser" v-model="mainDrawer" app disable-resize-watcher color="secondary" pt-0>
      <v-list dense>
        <v-list-item @click="navigate({ path: '/' })">
          <v-list-item-action class="menu-item">
            <v-icon>home</v-icon>
          </v-list-item-action>
          <v-list-item-content class="menu-item">
            <v-list-item-title>Home</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-for="item in menuList" :key="item.path" @click="navigate({ path: item.path })">
          <v-list-item-action class="menu-item">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-action>
          <v-list-item-content class="menu-item">
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="showCalendar" @click="clickHorsePlanner">
          <v-list-item-action class="menu-item">
            <v-icon>calendar_today</v-icon>
          </v-list-item-action>
          <v-list-item-content class="menu-item">
            <v-list-item-title>Horse Planner</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-group :value="false" prepend-icon="work_outline" color="info">
          <template #activator>
            <v-list-item-title>Workspaces</v-list-item-title>
          </template>
          <v-list-item v-for="(workspace, i) in liveWorkspaces" :key="i" link :class="{ active: workspace.name == currentWorkspace.name }" @click="changeWorkspace(workspace, route)">
            <v-list-item-title>{{ workspace.name }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
        <v-list-group v-if="isAlpha && archivedWorkspaces.length" :value="false" prepend-icon="work_outline" color="info">
          <template #activator>
            <v-list-item-title>Archived</v-list-item-title>
          </template>
          <v-list-item v-for="(workspace, i) in archivedWorkspaces" :key="i">
            <v-list-item-title>{{ workspace.name }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar v-if="currentUser && canShowContent" app color="secondary" style="z-index: 5">
      <v-btn icon @click.stop="mainDrawer = !mainDrawer">
        <v-icon>menu</v-icon>
      </v-btn>

      <v-chip small :color="settingsEnvironment[currentEnvironment].color" class="text-capitalize caption mr-2">
        {{ settingsEnvironment[currentEnvironment].label }}
      </v-chip>

      <v-flex v-if="reloadSearchBar" class="mx-2">
        <SearchBar />
      </v-flex>

      <v-flex class="mx-2 shrink">
        <WorkspaceSearchBar />
      </v-flex>

      <div class="mr-4">
        <Languages />
      </div>

      <div class="mr-4">
        <Workspaces />
      </div>

      <VersionSwitcher v-if="isLocal" />

      <WorkspaceActions v-if="isAlpha && canArchive && !['merch'].includes(currentWorkspace.code)" @archive-workspace="archiveWorkspace()" />

      <v-menu v-if="currentUser" v-model="userMenu" :close-on-content-click="false" :nudge-width="200" offset-x>
        <template #activator="{ on }">
          <v-btn icon v-on="on">
            <v-icon>person</v-icon>
          </v-btn>
        </template>
        <v-card light>
          <v-list>
            <v-list-item>
              <v-list-item-avatar>
                <v-icon>person</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ currentUser.firstName }}
                  {{ currentUser.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ currentUser.email }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="logout">Logout</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container v-if="canShowContent" fluid fill-height align-start class="pt-0 pb-0 px-6">
        <KeepAlive v-if="keepAlive" :include="include">
          <v-row no-gutters class="fill-height">
            <v-col col="12" class="fill-height">
              <router-view :key="$route.path" />
            </v-col>
          </v-row>
        </KeepAlive>
      </v-container>
      <v-container v-else fill-height justify-center>
        <v-progress-circular :width="3" :size="50" indeterminate color="blue" />
      </v-container>
    </v-main>

    <Notification />

    <DialogArchiveWorkspace v-if="canShowContent" :ref="(el) => refs.archivePopup = el" />
  </v-app>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';

import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';

import { useApolloClient } from '@vue/apollo-composable';

import SearchBar from './components/common/SearchBar.vue';
import { getLogoutUrl } from './utils/auth';
import VersionSwitcher from './components/common/VersionSwitcher.vue';
import Workspaces from './components/common/Workspaces.vue';
import WorkspaceSearchBar from '@/components/common/WorkspaceSearchBar.vue';
import Languages from './components/common/Languages.vue';
import Notification from './components/common/Notification.vue';
import WorkspaceActions from './components/common/WorkspaceActions.vue';
import DialogArchiveWorkspace from './components/workspaces/DialogArchiveWorkspace.vue';

import { getPermissions, findRestrictions } from './utils/permissions';
import { changeWorkspace, clearCacheKeepAlive, checkIfHasChanges } from './utils/pwa';
import { getGraphqlSchema } from './graphql/index';

const instance = getCurrentInstance();
const vuetify = instance.proxy.$vuetify;

const { resolveClient } = useApolloClient();
const apollo = resolveClient();

const store = useStore();
const router = useRouter();
const route = useRoute();

const mainDrawer = ref(false);
const userMenu = ref(false);

const menu = require('./configs/menu.json');

const settingsEnvironment = {
  test: { label: 'Test', color: 'grey' },
  production: { label: 'Production', color: 'orange' },
  staging: { label: 'Recette', color: 'green' }
};

const currentEnvironment = process.env.VUE_APP_ENV;

const isLocal = window.location.hostname === 'localhost';

const currentUser = computed(() => store.state.currentUser);

const theme = computed(() => (vuetify.theme.dark ? 'dark' : 'light'));

const workspaces = computed(() => store.getters.workspaces);

const currentWorkspace = computed(() => store.getters.workspace);

const showCalendar = computed(() => {
  if (store.state.environment.PROJECT !== 'alpha') {
    return false;
  }

  const showCalendarRestriction = findRestrictions(currentUser.value.restrictions, 'show_calendar', currentWorkspace.value.code, store.state.environment.PROJECT);

  return showCalendarRestriction.length;
});

const menuList = computed(() => {
  if (currentUser.value) {
    const allowedItems = menu.filter(menuItem => {
      const props = router.resolve({ name: menuItem.name });

      if (props.route.meta.allowedRoles) {
        if (props.route.meta.workspaces) {
          const canGo = getPermissions(props.route.meta.allowedRoles, currentUser.value.roles, currentWorkspace.value, props.route.meta.workspaces);

          if (canGo) {
            return menuItem;
          }
        }
      }
    });
    return allowedItems;
  }
  return [];
});

const canShowContent = computed(() => route.name === 'login' || store.state.loaded);

const keepAlive = computed(() => store.state.keepAlive);

const reloadSearchBar = computed(() => store.state.reloadSearchBar);

const include = computed(() => store.state.include);

const clickHorsePlanner = () => {
  alert('HORSE PLANNER');
};

const navigate = path => {
  if (path.path === route.path) {
    return;
  }

  checkIfHasChanges(() => {
    mainDrawer.value = false;
    clearCacheKeepAlive();
    router.push(path);
  });
};

const logout = () => {
  mainDrawer.value = false;

  checkIfHasChanges(() => {
    userMenu.value = false;

    window.localStorage.clear();
    window.location.href = getLogoutUrl(window.location.origin, store.getters.enviroment.VUE_APP_SSO_CLIENT_ID, window.location.origin);
  });
};

const liveWorkspaces = computed(() => store.getters.liveWorkspaces);
const archivedWorkspaces = computed(() => store.getters.archivedWorkspaces);
const isAlpha = computed(() => store.state.environment.PROJECT === 'alpha');
const canArchive = currentUser.value?.roles.includes('admin');

const refs = ref({});
const archiveWorkspace = () => {
  refs.value.archivePopup.open();
};

store.commit('setLoaded', false);

apollo
  .query({
    query: getGraphqlSchema('appLoad'),
    fetchPolicy: 'network-only' // disable cache, necessary when we make login/logout/login
  })
  .then(result => {
    // If current user doesn't have information
    if (!result.data.currentUser) {
      throw new Error('No current user data');
    }

    // Saves on the store our logic data
    try {
      const allWorkspace = result.data.getAllWorkspaces ? result.data.getAllWorkspaces.items : [];
      store.commit('setCloudinaryConfig', result.data);
      store.commit('setCurrentUser', result.data.currentUser);
      store.commit('setVersions', result.data.versions);
      store.commit('setVersion', store.getters.version);
      store.commit('setRawContentTypes', result.data.schema.types);
      store.commit('setReferences', result.data.schema.references);
      store.commit('setWorkflows', result.data.schema.workflows);
      store.commit('setWorkspaces', result.data.workspaces.items);
      store.commit('setAllWorkspaces', allWorkspace);
    } catch (e) {
      console.log('[ERROR] ->', e);
    }

    store.commit('setLoaded', true);
  }).catch(error => {
    console.error('appLoad error:', error);
  });

</script>

<style lang="scss" rel="stylesheet/scss">
.row {
  margin: 0 !important;
}

.top-bar {
  z-index: 8;
}

.move-cursor {
  cursor: move;
}

.workspace-items {
  min-height: 0 !important;
  white-space: normal !important;
}

.active {
  background-color: #343434;
}

/**************************
    SCROLLBAR
  **************************/

/* width */
::-webkit-scrollbar {
  width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
  background: #343434;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #a8a8a8;
}

/* Handle on hover */
// ::-webkit-scrollbar-thumb:hover {

// }

/* TO REMOVE ON LIGHT THEME */
.switcher.v-input--is-label-active .v-input--switch__track {
  color: #4caf50 !important;
}

.switcher .v-input--switch__track {
  color: #ff5252 !important;
}

.switcher.v-input--is-label-active .v-input--switch__thumb {
  color: #bdbdbd !important;
}

.switcher label {
  margin-bottom: 0;
}

/* Entity and Picker Multivalued background */
.wt-odd {
  background-color: #424242;
}

.wt-even {
  background-color: #292929;
}

.wt-odd,
.wt-even {
  cursor: move;
}

.contextMenu {
  position: absolute;
  top: 0;
}

.border-radius-0 {
  border-radius: 0 !important;
}

.item-selected {
  background-color: #a8a8a8;
}

.border-radius-0 {
  border-radius: 0;
}

// Shake message

.wiggle {
  animation: wiggle 600ms;
  animation-timing-function: linear;
}

@keyframes wiggle {
  0% {
    transform: translate(0, 0);
  }

  50% {
    transform: translate(0, 0);
  }

  60% {
    transform: translate(3px, 0);
  }

  70% {
    transform: translate(-3px, 0);
  }

  80% {
    transform: translate(3px, 0);
  }

  90% {
    transform: translate(-3px, 0);
  }

  100% {
    transform: translate(3px, 0);
  }
}

.v-application.fr-fullscreen-wrapper {
  z-index: 50 !important;
}
</style>
